import Head from "next/head";
import CLMHero2 from "components/CLM/CLMHero2";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import FAQ2 from "components/CLM/FAQ2";
import TestimonialSlider from "components/CLM/TestimonialSliderSection";
import Image from "next/image";
import { BASE_ASSETS_URL } from "@constants/constants";

const CLMHeroData = {
  // title: "Reporting",
  heading: "Certius, your contract assistant",
  description:
    "Instantly create templates, detect fields, and get signatures faster, cutting review cycles in half.",
  // image: `${BASE_ASSETS_URL}/v2/clm/clm/certius-hero.gif"),
  video: `${BASE_ASSETS_URL}/v2/clm/videos/certius-hero.mp4`,
  btnText: "Contact Sales",
  btnUrl: "/clm/request-demo",
};

const certiusFaqData = [
  {
    question:
      "What is Certius, and how does it support enterprise contract teams?",
    answer:
      "Certius is BoloSign’s AI-powered legal assistant designed to streamline drafting, review, redlining, and metadata extraction. It helps legal and procurement teams work faster, more accurately, and with greater visibility across high-volume workflows.",
  },
  {
    question:
      "How secure is Certius when working with sensitive legal documents?",
    answer:
      "Certius is built with enterprise-grade security protocols, including data encryption at rest and in transit, access controls, and SOC 2/ISO 27001 compliance. Your contracts stay protected—always.",
  },
  {
    question:
      "Can Certius adapt to our company’s preferred language and legal playbooks?",
    answer:
      "Yes. Certius learns from your redlines, playbooks, and past negotiations. It suggests edits aligned with your preferred language, risk posture, and approval guardrails, just like an in-house legal team member would.",
  },
  {
    question:
      "How accurate is the AI when reviewing contracts for risk or redlines?",
    answer:
      "Certius leverages legal-trained LLMs and contract-specific agents to ensure high-quality outputs. It can highlight red flags, auto-suggest fallback language, and explain changes in simple terms, ensuring compliance without slowing you down.",
  },
  {
    question: "What types of contract data can Certius extract automatically?",
    answer:
      "Certius auto-identifies over 100+ metadata fields—like party names, contract values, renewal terms, jurisdiction, indemnity clauses, and more. You can search, filter, and report instantly—no manual tagging required.",
  },
  {
    question:
      "Can Certius help us draft contracts, emails, or responses faster?",
    answer:
      "Absolutely. From drafting standard NDAs to summarizing a 50-page MSA or generating an email to external counsel, Certius saves hours by handling first drafts with legal-aware prompts and contextual understanding.",
  },
  {
    question:
      "Is Certius customizable to our approval workflows and integrations?",
    answer:
      "Yes. Certius can be integrated into your existing CLM workflows and customized with your approval chains, clause libraries, and document types. It also works seamlessly alongside your repository, signature flow, and third-party tools.",
  },
];

const CertiusAI = () => {
  return (
    <>
      <Head>
        <title>
          Certius AI-Powered Contract Management | BoloSign CLM Software
        </title>
        <meta
          name="description"
          content="Certius is BoloSign’s AI-powered contract management assistant that helps legal teams draft, review, and automate contracts faster with built-in CLM. Learn more!"
        />
        <meta
          name="keywords"
          content="Certius, AI contract management, contract automation, AI-powered CLM, contract review AI, legal tech, contract drafting AI, BoloSign Certius"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/certius-ai/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />

      <EqualDivisionSection
        // title=""
        heading="Convert any contract into a reusable template"
        description="Certius auto-detects fields, variables, and placeholders—so your team can turn static documents into dynamic, ready-to-send templates in minutes."
        checks={[
          "Create templates from existing PDFs or Word files.",
          "Get smart suggestions for fields and variables.",
          "Maintain control with centralized access, permissions, and version history.",
        ]}
        // image={`${BASE_ASSETS_URL}/v2/clm/clm/certius-sales.gif")}
        video={`${BASE_ASSETS_URL}/v2/clm/videos/certius-sales.mp4`}
        bgColor="bg-cream"
        imageRight={true}
        leftDivStyle="items-center"
      />

      <EqualDivisionSection
        // title=""
        heading="Extract key contract data in seconds with AI"
        description="Certius instantly pulls out the key data from every contract—like names, values, dates, and clauses—so you don’t have to dig through pages ever again."
        checks={[
          "No manual tagging or data entry.",
          "Auto-detects 100+ metadata points instantly.",
          "Filter, track, and report on any clause or field.",
          "Manage metadata at scale across teams and contracts.",
        ]}
        // image={`${BASE_ASSETS_URL}/v2/clm/clm/certius-services.gif")}
        video={`${BASE_ASSETS_URL}/v2/clm/videos/certius-services.mp4`}
        bgColor=""
        imageRight={false}
        leftDivStyle="items-center md:-mt-20"
      />

      {/* this section is pending here might not need or use same as used in ai-verification */}
      {/* <section className="bg-black">
        <div>
          <p>
            BoloSign’s AI makes finding important contract details incredibly
            easy. My team can instantly access key information like renewal
            dates, contract values, and business owners, without ever leaving
            the platform.
          </p>
          <div>
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/circularise.svg")}
              alt="Company Logo"
              width={200}
              height={200}
            />
            <div>
              <p>– Rebecca Miller</p>
              <p>Director of Legal Operations at ClearPath Solutions</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* might not need on this page */}
      <TestimonialSlider />

      <EqualDivisionSection
        // title=""
        heading="Review contracts faster and smarter"
        description="Certius helps legal teams cut review time with smart suggestions, instant redlines, and built-in compliance checks."
        checks={[
          "Instant, precise clause recommendations.",
          "Automated risk and compliance detection.",
          "Expert-built review checklists.",
          "AI-driven custom review playbooks.",
        ]}
        // image={`${BASE_ASSETS_URL}/v2/clm/clm/certius-services.gif")}
        video={`${BASE_ASSETS_URL}/v2/clm/videos/certius-review.mp4`}
        bgColor="bg-cream"
        imageRight={true}
        leftDivStyle="items-center"
      />

      <section className="bg-cream text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={certiusFaqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default CertiusAI;
